import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useTenantSlug } from '../hooks/useTenant';
import tenantService from '../services/tenantService';
import useTenantTableScheme from '../hooks/useTenantTableScheme';

import { getApiUrl } from '../utils/runtimeConfig';
import { logError } from '../utils/logger';

const API_BASE_URL = getApiUrl();

/**
 * Löst eine relative Logo-URL gegen die API-Base-URL auf.
 * Das Backend liefert z.B. "/api/tenants/mvg/logo", aber der Browser
 * muss die volle URL verwenden (z.B. "https://api.rentinger.de/api/tenants/mvg/logo").
 */
const resolveLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  // API_BASE_URL endet auf "/api", logoUrl beginnt mit "/api/..."
  const apiOrigin = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${apiOrigin}${logoUrl}`;
};

/**
 * Context für Tenant-Branding-Informationen
 * Stellt tenantspezifisches Logo und Namen für die gesamte Anwendung bereit
 */
const TenantBrandingContext = createContext({
  name: '',
  logoUrl: null,
  theme: null,
  navbar: null,
  tableScheme: null,
  loading: true,
  error: null,
  refetch: () => {}
});

/**
 * Hook zum Zugriff auf Tenant-Branding-Daten
 * @returns {{name: string, logoUrl: string|null, theme: Object|null, navbar: Object|null, loading: boolean, error: string|null, refetch: Function}}
 */
export const useTenantBranding = () => {
  const context = useContext(TenantBrandingContext);
  if (!context) {
    throw new Error('useTenantBranding muss innerhalb von TenantBrandingProvider verwendet werden');
  }
  return context;
};

/**
 * Provider für Tenant-Branding-Context
 * Lädt Branding-Daten beim Mount basierend auf tenantSlug aus URL
 *
 * FIXES:
 * - Memory Leak Prevention: isMounted flag verhindert setState nach Unmount
 * - Cache-Invalidierung: Cache wird bei Tenant-Wechsel geleert
 */
export const TenantBrandingProvider = ({ children }) => {
  const tenantSlug = useTenantSlug();
  const previousSlugRef = useRef(null);
  const [branding, setBranding] = useState({
    name: '',
    logoUrl: null,
    theme: null,
    navbar: null,
    tableScheme: null,
    tableSchemeSlug: null,
    loading: true,
    error: null
  });

  // Refetch-Funktion für manuelle Aktualisierung
  const refetch = useCallback(() => {
    if (tenantSlug) {
      tenantService.clearCacheForSlug(tenantSlug);
    }
  }, [tenantSlug]);

  useEffect(() => {
    // Flag zur Vermeidung von Memory Leaks bei unmounted Components
    let isMounted = true;

    const loadBranding = async () => {
      try {
        if (isMounted) {
          setBranding(prev => ({ ...prev, loading: true, error: null }));
        }

        // Cache bei Tenant-Wechsel invalidieren
        if (previousSlugRef.current && previousSlugRef.current !== tenantSlug) {
          tenantService.clearCacheForSlug(previousSlugRef.current);
        }
        previousSlugRef.current = tenantSlug;

        const data = await tenantService.getBranding(tenantSlug);

        // Nur State setzen wenn Component noch gemountet ist
        if (isMounted) {
          setBranding({
            name: data.name || '',
            logoUrl: resolveLogoUrl(data.logoUrl),
            theme: data.theme || null,
            navbar: data.navbar || null,
            tableScheme: data.tableScheme || null,
            tableSchemeSlug: tenantSlug,
            loading: false,
            error: null
          });
        }
      } catch (error) {
        logError('Fehler beim Laden des Tenant-Brandings:', error);

        // Nur State setzen wenn Component noch gemountet ist
        if (isMounted) {
          setBranding({
            name: '',
            logoUrl: null,
            theme: null,
            navbar: null,
            tableScheme: null,
            tableSchemeSlug: null,
            loading: false,
            error: error.message || 'Branding konnte nicht geladen werden'
          });
        }
      }
    };

    if (tenantSlug) {
      loadBranding();
    }

    // Cleanup-Funktion zur Vermeidung von Memory Leaks
    return () => {
      isMounted = false;
    };
  }, [tenantSlug]);

  // Tenant-Farbschema der Tabellen/Panels als CSS-Variablen auf <html> (einmal zentral)
  // Nur für den Slug, zu dem das Schema geladen wurde: bei Slug-Wechsel/leerem Slug sofort entfernt.
  useTenantTableScheme(tenantSlug && branding.tableSchemeSlug === tenantSlug ? branding.tableScheme : null);

  // Memoized Context-Value zur Vermeidung unnötiger Re-Renders
  const contextValue = React.useMemo(() => ({
    ...branding,
    refetch
  }), [branding, refetch]);

  return (
    <TenantBrandingContext.Provider value={contextValue}>
      {children}
    </TenantBrandingContext.Provider>
  );
};
